import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';

import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsValidationInputMixin from '../../mixins/ids-validation-mixin/ids-validation-input-mixin';
import IdsElement from '../../core/ids-element';
import IdsLabelStateParentMixin from '../../mixins/ids-label-state-mixin/ids-label-state-parent-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsLoadingIndicator from '../ids-loading-indicator/ids-loading-indicator';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import { IdsPopupElementRef } from '../ids-popup/ids-popup-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import '../ids-modal/ids-modal';
import '../ids-search-field/ids-search-field';
import '../ids-trigger-field/ids-trigger-field';

import type IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type IdsTriggerButton from '../ids-trigger-field/ids-trigger-button';
import type IdsModalButton from '../ids-modal/ids-modal-button';
import type IdsDataGrid from '../ids-data-grid/ids-data-grid';
import type { IdsDataGridColumn } from '../ids-data-grid/ids-data-grid-column';
import type { IdsDataGridSettings } from '../ids-data-grid/ids-data-grid-settings';
import type IdsLayoutGrid from '../ids-layout-grid/ids-layout-grid';
import type IdsModal from '../ids-modal/ids-modal';
import type IdsSearchField from '../ids-search-field/ids-search-field';
import type IdsText from '../ids-text/ids-text';
import styles from './ids-lookup.scss';

const Base = IdsAutoSelectMixin(
  IdsDirtyTrackerMixin(
    IdsLabelStateParentMixin(
      IdsLocaleMixin(
        IdsKeyboardMixin(
          IdsValidationInputMixin(
            IdsLabelPositionMixin(
              IdsFieldHeightMixin(
                IdsTooltipMixin(
                  IdsEventsMixin(
                    IdsElement
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Lookup Component
 * @type {IdsLookup}
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsLabelStateParentMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsTooltipMixin
 * @mixes IdsAutoSelectMixin
 * @part trigger-field - the trigger container
 * @part input - the input element
 * @part trigger-button - the trigger button
 * @part icon - the icon in the trigger button
 * @part modal - the modal dialog container
 * @part data-grid - the dataGrid element
 */
@customElement('ids-lookup')
@scss(styles)
export default class IdsLookup extends Base {
  applyButton?: IdsModalButton | null;

  dataGrid?: IdsDataGrid | null;

  listBox?: any;

  searchField?: IdsSearchField | null;

  state = {
    clearable: true,
    dataGridSettings: { rowSelection: 'multiple' },
    value: '',
    title: ''
  };

  /**
   * Get the modal element
   * @returns {IdsModal | null} the modal element
   */
  get modal(): IdsModal | null {
    return this.querySelector('[slot="lookup-modal"]') || this.shadowRoot?.querySelector('ids-modal') || null;
  }

  constructor() {
    super();

    // Override global html title
    Object.defineProperty(this, 'title', {
      get: () => this.#title,
      set: (value) => { this.#title = value; },
      enumerable: true,
      configurable: true
    });
  }

  isFormComponent = true;

  /**
   * Get the trigger field element
   * @returns {IdsTriggerField | null} the trigger field
   */
  get triggerField(): IdsTriggerField | null {
    return this.shadowRoot?.querySelector<IdsTriggerField>('ids-trigger-field') ?? null;
  }

  /**
   * Get the trigger button element
   * @returns {IdsTriggerButton | null} the trigger button
   */
  get triggerButton(): IdsTriggerButton | null {
    return this.shadowRoot?.querySelector<IdsTriggerButton>('ids-trigger-button[part="trigger-lookup"]') ?? null;
  }

  /**
   * Target the inner trigger-field's field container for tooltip positioning
   * @returns {HTMLElement} The field container element
   */
  onTooltipTargetDetection(): HTMLElement {
    return this.triggerField?.fieldContainer || this.triggerField || this;
  }

  /**
   * Get the datagrid's loading indicator element
   * @returns {IdsLoadingIndicator | null} the loading element
   */
  get loadingIndicator(): IdsLoadingIndicator | null {
    return this.dataGrid?.loadingIndicator || null;
  }

  /**
   * Get the inner ids-text element which contains the label text
   * @returns {IdsText | null} the label text element
   */
  get labelTextEl(): IdsText | null {
    return this.input?.labelEl?.querySelector('ids-text') || null;
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();

    this.applyButton = this.shadowRoot?.querySelector('#modal-confirm-btn');
    this.searchField = this.shadowRoot?.querySelector<IdsSearchField>('ids-search-field');

    // Setup Attached Datagrid
    this.dataGrid = this.shadowRoot?.querySelector('ids-data-grid');
    this.dataGrid?.setAttribute(attributes.LIST_STYLE, 'true');

    const dataGridParentContainer = this.shadowRoot?.querySelector<IdsLayoutGrid>('.data-grid-parent-container');

    this.onEvent('started.loading-indicator', dataGridParentContainer, () => {
      if (dataGridParentContainer) dataGridParentContainer.autoFit = false;
    });

    this.onEvent('stopped.loading-indicator', dataGridParentContainer, () => {
      if (dataGridParentContainer) dataGridParentContainer.autoFit = true;
    });

    if (this.height) {
      this.setContainerHeight(this.height);
    }

    this
      .#handleEvents()
      .#handleKeys();
  }

  mountedCallback(): void {
    if (this.modal) {
      // Link the Modal to its trigger button (sets up click/focus events)
      this.modal.target = this.triggerButton as IdsPopupElementRef;
      this.modal.triggerType = 'click';
    }

    if (this.state.dataGridSettings) {
      this.dataGridSettings = this.state.dataGridSettings;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();

    this.dataGrid = undefined;
    this.searchField = undefined;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.AUTOCOMPLETE,
      attributes.CLEARABLE,
      attributes.DISABLED,
      attributes.FIELD,
      attributes.ID,
      attributes.HEIGHT,
      attributes.LINE_CLAMP,
      attributes.NO_MARGINS,
      attributes.PAGINATION,
      attributes.PAGE_NUMBER,
      attributes.PAGE_SIZE,
      attributes.PLACEHOLDER,
      attributes.READONLY,
      attributes.SEARCHABLE,
      attributes.SEARCHFIELD_PLACEHOLDER,
      attributes.RECORD_COUNT,
      attributes.SIZE,
      attributes.SUPPRESS_VALIDATION,
      attributes.TABBABLE,
      attributes.TITLE,
      attributes.VALUE
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const label = this.label || '';

    return `
    <ids-trigger-field
      ${label ? ` label="${label}"` : ''}
      part="trigger-field"
      value="${this.value === undefined ? '' : this.value}"
      ${this.placeholder ? ` placeholder="${this.placeholder}"` : ''}
      ${this.autocomplete ? ` autocomplete search-field="${this.field}"` : ''}
      ${this.clearable ? ' clearable="true"' : ''}
      ${this.disabled ? ' disabled="true"' : ''}
      ${this.readonly ? ' readonly="true"' : ''}
      ${this.dirtyTracker ? ' dirty-tracker="true"' : ''}
      ${this.compact ? ' compact' : ''}
      ${this.size ? ` size="${this.size}"` : ''}
      ${this.fieldHeight ? ` field-height="${this.fieldHeight}"` : ''}
      ${this.labelPosition ? ` label-position="${this.labelPosition}"` : ''}
      ${this.labelWidth ? ` label-width="${this.labelWidth}"` : ''}
      ${this.labelAlignY ? ` label-align-y="${this.labelAlignY}"` : ''}
      ${this.labelBreak ? ` label-break="${this.labelBreak}"` : ''}
      ${this.validate ? ` validate="${this.validate}"` : ''}
      ${this.validate && this.validationEvents ? ` validation-events="${this.validationEvents}"` : ''}>
      <ids-trigger-button
        slot="trigger-end"
        part="trigger-lookup"
        disabled="${this.disabled}"
        readonly="${this.readonly}">
        <ids-text audible="true">LookupTriggerButton</ids-text>
        <ids-icon icon="search-list" part="icon"></ids-icon>
      </ids-trigger-button>
    </ids-trigger-field>
    <slot name="lookup-modal">
      <ids-modal id="lookup-modal" aria-labelledby="lookup-modal-title" part="modal">
        <ids-text slot="title" font-size="24" type="h2" id="lookup-modal-title">${this.title}</ids-text>
        ${this.searchable ? `<ids-search-field clearable label="Search Field with Hidden Label" label-state="collapsed" size="full" placeholder="${this.searchfieldPlaceholder}"></ids-search-field>` : ''}
        <ids-layout-grid class="data-grid-parent-container" auto-fit="false" gap="md" no-margins="true" min-col-width="400px">
          <ids-layout-grid-cell class="data-grid-container${this.searchable ? ' has-search' : ''}" col-span="12">
              <ids-data-grid id="lookup-data-grid" label="${this.label}" part="data-grid" auto-fit="true"${this.pagination ? ` pagination="${this.pagination}"` : ''}${this.pageSize ? ` page-size="${this.pageSize}"` : ''}${this.pageNumber ? ` page-number="${this.pageNumber}"` : ''}>
              </ids-data-grid>
          </ids-layout-grid-cell>
        </ids-layout-grid>

        <ids-button-group class="lookup-group" slot="buttons" direction="row-reverse">
          <ids-button-group-section gap="8">
            <ids-modal-button cancel id="modal-cancel-btn" appearance="secondary">
              <span>Cancel</span>
            </ids-modal-button>
            <ids-modal-button confirm id="modal-confirm-btn" appearance="primary">
              <span>Apply</span>
            </ids-modal-button>
          </ids-button-group-section>
        </ids-button-group>
      </ids-modal>
    </slot>
    `;
  }

  /**
   * Set autocomplete attribute
   * @param {boolean | string} value autocomplete value
   */
  set autocomplete(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.AUTOCOMPLETE, '');
      this.input?.setAttribute(attributes.AUTOCOMPLETE, '');
      this.input?.setAttribute(attributes.SEARCH_FIELD, this.field);
    } else {
      this.removeAttribute(attributes.AUTOCOMPLETE);
      this.input?.removeAttribute(attributes.AUTOCOMPLETE);
    }
  }

  /**
   * Get the autocomplete attribute
   * @returns {boolean | string} true if autocomplete is enabled
   */
  get autocomplete(): boolean | string {
    return this.hasAttribute(attributes.AUTOCOMPLETE);
  }

  /**
   * Callback for dirty tracker setting change
   * @param {boolean} value The changed value
   * @returns {void}
   */
  onDirtyTrackerChange(value: boolean) {
    if (value) this.container?.setAttribute(attributes.DIRTY_TRACKER, value.toString());
    else this.container?.removeAttribute(attributes.DIRTY_TRACKER);
  }

  /**
   * Set the value of the dropdown using the value/id attribute if present
   * @param {string} value The value/id to use
   */
  set value(value: string) {
    if (value !== this.state.value) {
      this.state.value = (value ?? '').toString();
      this.setAttribute(attributes.VALUE, this.state.value);

      // Update trigger field value
      if (this.triggerField) {
        const tfValue = this.triggerField.value;
        if (tfValue !== this.state.value) {
          this.triggerField.value = this.state.value;
        }
      }
    }
  }

  /**
   * Get the current value
   * @returns {string} the current value
   */
  get value(): string { return this.state.value; }

  /**
   * Callback for label setting change
   * @returns {void}
   */
  onLabelChange(): void {
    if (this.input) this.input.label = this.label;
    this.updateLabelTooltip();
  }

  /**
   * Push label-state to the container element
   * @returns {void}
   */
  onLabelStateChange(): void {
    if (this.input) this.input.labelState = this.labelState;
  }

  /**
   * Callback for label required setting change
   * @returns {void}
   */
  onLabelRequiredChange(): void {
    if (this.input) this.input.labelRequired = this.labelRequired;
  }

  /**
   * Sets the readonly state of the field
   * @param {boolean | string} value string value from the readonly attribute
   */
  set readonly(value: boolean | string) {
    const isReadonly = stringToBool(value);
    if (!this.triggerField) {
      return;
    }

    if (isReadonly) {
      this.removeAttribute('disabled');
      this.triggerField.readonly = true;
      this.setAttribute('readonly', 'true');
      return;
    }

    this.triggerField.readonly = false;
    this.removeAttribute('readonly');
  }

  /**
   * Get the readonly state
   * @returns {string | boolean} true if readonly
   */
  get readonly(): string | boolean {
    return stringToBool(this.getAttribute('readonly')) || false;
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    const isDisabled = stringToBool(value);
    if (!this.triggerField) {
      return;
    }

    if (isDisabled) {
      this.removeAttribute('readonly');

      this.triggerField.disabled = true;
      this.setAttribute('disabled', 'true');
      return;
    }

    this.triggerField.disabled = false;
    this.removeAttribute('disabled');
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} true if disabled
   */
  get disabled(): boolean | string {
    return stringToBool(this.getAttribute('disabled')) || false;
  }

  /**
   * Set the trigger button to tabbable
   * @param {boolean | string} value True of false depending if the trigger field is tabbable
   */
  set tabbable(value: boolean | string) {
    const isTabbable = stringToBool(value);
    if (!this.triggerField) {
      return;
    }

    if (isTabbable) {
      this.setAttribute(attributes.TABBABLE, String(isTabbable));
      this.triggerField.setAttribute(attributes.TABBABLE, String(isTabbable));
    } else {
      this.removeAttribute(attributes.TABBABLE);
      this.triggerField.removeAttribute(attributes.TABBABLE);
    }
  }

  /**
   * Get whether the trigger button is tabbable
   * @returns {string | boolean} true if tabbable
   */
  get tabbable(): string | boolean {
    const attr = this.getAttribute(attributes.TABBABLE);
    if (this.readonly || this.disabled) {
      return false;
    }
    if (attr === null) {
      return false;
    }
    return stringToBool(this.getAttribute(attributes.TABBABLE));
  }

  /**
   * Set the pagination type
   * @param {string} value The pagination type (client-side or server-side)
   */
  set pagination(value: string) {
    if (value && (value === 'client-side' || value === 'server-side')) {
      this.setAttribute(attributes.PAGINATION, value);
    } else if (!value) {
      this.removeAttribute(attributes.PAGINATION);
    }
  }

  /**
   * Get the pagination type
   * @returns {string} the pagination type
   */
  get pagination(): string {
    return this.getAttribute(attributes.PAGINATION) || '';
  }

  /**
   * Set the page size
   * @param {string | number} value The page size
   */
  set pageSize(value: string | number) {
    const numValue = Number(value);
    if (value && !Number.isNaN(numValue) && numValue > 0) {
      this.setAttribute(attributes.PAGE_SIZE, value.toString());
    } else if (!value) {
      this.removeAttribute(attributes.PAGE_SIZE);
    }
  }

  /**
   * Get the page size
   * @returns {string} the page size
   */
  get pageSize(): string {
    return this.getAttribute(attributes.PAGE_SIZE) || '';
  }

  /**
   * Set the page number
   * @param {string | number} value The page number
   */
  set pageNumber(value: string | number) {
    const numValue = Number(value);
    if (value && !Number.isNaN(numValue) && numValue > 0) {
      this.setAttribute(attributes.PAGE_NUMBER, value.toString());
    } else if (!value) {
      this.removeAttribute(attributes.PAGE_NUMBER);
    }
  }

  /**
   * Get the page number
   * @returns {string} the page number
   */
  get pageNumber(): string {
    return this.getAttribute(attributes.PAGE_NUMBER) || '';
  }

  /**
   * Set the columns array of the data grid
   * @param {IdsDataGridColumn[] | undefined} value The array to use
   */
  set columns(value: IdsDataGridColumn[] | undefined) {
    if (this.dataGrid) this.dataGrid.columns = value;
  }

  /**
   * Get the columns array
   * @returns {any[] | undefined} the columns array
   */
  get columns(): any[] | undefined { return this.dataGrid?.columns; }

  /**
   * Set the data array of the data grid
   * @param {Array<Record<string, any>>} value The array to use
   */
  set data(value: Array<Record<string, any>>) {
    if (this.dataGrid) this.dataGrid.data = value;
    this.#syncSelectedRows();
  }

  /**
   * Get the data array
   * @returns {Array<Record<string, any>>} the data array
   */
  get data(): Array<Record<string, any>> { return this.dataGrid ? this.dataGrid.data : []; }

  /**
   * Get the search term case sensitivity setting
   * @returns {boolean | string} true if search terms are case sensitive
   */
  get searchTermCaseSensitive(): boolean | string {
    const attr = this.getAttribute(attributes.SEARCH_TERM_CASE_SENSITIVE);
    if (attr === null) {
      return false;
    }
    return stringToBool(this.getAttribute(attributes.SEARCH_TERM_CASE_SENSITIVE));
  }

  /**
   * Set the search term case sensitivity
   * @param {boolean | string} value whether search terms should be case sensitive
   */
  set searchTermCaseSensitive(value: boolean | string) {
    const isCaseSensitive = stringToBool(value);
    this.setAttribute(attributes.SEARCH_TERM_CASE_SENSITIVE, String(isCaseSensitive));
  }

  /**
   * Sync the selected rows in the dataGrid
   * @private
   * @param {string} value The value to be set
   * @returns {void}
   */
  #syncSelectedRows(value: string = this.value): void {
    // Deselect all rows, if given value is empty
    if (value === '') {
      if (this.dataGrid?.selectedRows?.length) this.dataGrid.deSelectAllRows();
      if (this.value !== value) this.value = value;
      return;
    }

    // Select the rows, if not selected already for given value split with delimiter
    // eslint-disable-next-line eqeqeq
    const findIndex = (d: any, v: string) => (d?.findIndex((r: any) => r[this.field] == v));
    const values = value?.split(this.delimiter) || [];
    let notFound: number[] = [];
    values.forEach((v: string, i: number) => {
      const dataIndex = findIndex(this.dataGrid?.data, v);
      if (dataIndex > -1) {
        if (findIndex(this.dataGrid?.selectedRows.map((d: any) => d.data), v) === -1) {
          this.dataGrid?.selectRow(dataIndex);
        }
      } else {
        notFound.push(i);
      }
    });
    notFound.forEach((i: number) => values.splice(i, 1));

    // Deselect rows, if any extra previously selected in grid
    notFound = [];
    if (this.dataGrid?.selectedRows && this.dataGrid?.selectedRows.length > values.length) {
      this.dataGrid?.selectedRows.forEach((d: any) => {
        if (!values.includes(d.data[this.field])) notFound.push(d.index);
      });
    }
    notFound.forEach((i: number) => this.dataGrid?.deSelectRow(i));

    // Update the value
    if (this.value !== value) this.value = value;
  }

  /**
   * Set any number of dataGrid settings
   * @param {object} settings The settings to use
   */
  set dataGridSettings(settings: IdsDataGridSettings) {
    this.state.dataGridSettings = settings;

    // Apply the settings to the grid
    for (const [setting, settingValue] of Object.entries(settings)) {
      (this.dataGrid as any)[setting] = settingValue;
    }
    if (this.dataGrid) this.dataGrid.listStyle = true;
    if (this.dataGridSettings.rowSelection === 'single') this.applyButton?.remove();
  }

  /**
   * Get the data grid settings
   * @returns {any} the data grid settings
   */
  get dataGridSettings(): any { return this.state.dataGridSettings; }

  /**
   * Sets the validation check to use
   * @param {string} value The `validate` attribute
   */
  set validate(value: string | null) {
    if (value) {
      this.setAttribute(attributes.VALIDATE, value.toString());
      this.triggerField?.setAttribute(attributes.VALIDATE, value.toString());
      this.triggerField?.setAttribute(attributes.VALIDATION_EVENTS, this.validationEvents);
      this.triggerField?.handleValidation?.();
    } else {
      this.removeAttribute(attributes.VALIDATE);
      this.triggerField?.removeAttribute(attributes.VALIDATE);
      this.triggerField?.removeAttribute(attributes.VALIDATION_EVENTS);
      this.triggerField?.handleValidation?.();
    }
  }

  /**
   * Get the validation check
   * @returns {string | null} the validate attribute
   */
  get validate(): string | null { return this.getAttribute(attributes.VALIDATE); }

  /**
   * Set `validation-events` attribute
   * @param {string} value The `validation-events` attribute
   */
  set validationEvents(value: string) {
    if (value) {
      this.setAttribute(attributes.VALIDATION_EVENTS, value.toString());
      this.triggerField?.setAttribute(attributes.VALIDATION_EVENTS, value.toString());
    } else {
      this.removeAttribute(attributes.VALIDATION_EVENTS);
      this.triggerField?.removeAttribute(attributes.VALIDATION_EVENTS);
    }
  }

  /**
   * Get the validation events
   * @returns {string} the validation events (default 'change blur')
   */
  get validationEvents(): string { return this.getAttribute(attributes.VALIDATION_EVENTS) || 'change blur'; }

  /**
   * Set the modal title
   * @param {string} value The modal title attribute
   */
  set #title(value: string) {
    if (value) {
      this.setAttribute(attributes.TITLE, ''); // avoids a browser tooltip
      this.state.title = value;
      this.#setTitleAndCount();
    }
  }

  /**
   * Get the modal title
   * @returns {string} the modal title
   */
  get #title(): string { return this.state.title || `${this.label}`; }

  /**
   * Set the field to use when populating the input
   * @param {string} value The field name
   */
  set field(value: string) {
    if (value) {
      this.setAttribute(attributes.FIELD, value);
      if (this.autocomplete) {
        this.input?.setAttribute(attributes.SEARCH_FIELD, value);
      }
    }
  }

  /**
   * Get the field to use when populating the input
   * @returns {string} the field name (default 'id')
   */
  get field(): string { return this.getAttribute(attributes.FIELD) || 'id'; }

  /**
   * Add a searchfield to the modal to search
   * @param {boolean | string} value set to true to make searchable
   */
  set searchable(value: boolean | string) {
    if (value) {
      this.setAttribute(attributes.SEARCHABLE, '');
    } else {
      this.removeAttribute(attributes.SEARCHABLE);
    }
  }

  /**
   * Get whether the modal has a search field
   * @returns {boolean | string} true if searchable
   */
  get searchable(): boolean | string { return this.hasAttribute(attributes.SEARCHABLE) || false; }

  /**
   * Set the searchfield placeholder text
   * @param {string} value placeholder contents
   */
  set searchfieldPlaceholder(value: string) {
    if (value) {
      this.setAttribute(attributes.SEARCHFIELD_PLACEHOLDER, value);
    } else {
      this.removeAttribute(attributes.SEARCHFIELD_PLACEHOLDER);
    }
  }

  /**
   * Get the searchfield placeholder text
   * @returns {string} the placeholder text
   */
  get searchfieldPlaceholder(): string { return this.getAttribute(attributes.SEARCHFIELD_PLACEHOLDER) || ''; }

  /**
   * Set the record count on the title area
   * @param {string} value placeholder contents
   */
  set recordCount(value: string) {
    if (value) {
      this.setAttribute(attributes.RECORD_COUNT, value);
      this.#setTitleAndCount();
    } else {
      this.removeAttribute(attributes.RECORD_COUNT);
    }
  }

  /**
   * Get the record count
   * @returns {string} the record count
   */
  get recordCount(): string { return this.getAttribute(attributes.RECORD_COUNT) || ''; }

  /**
   * Set the record count with the title when updated
   */
  #setTitleAndCount() {
    const titleElem = this.modal?.querySelector<IdsText>('[slot="title"]');
    if (titleElem) titleElem.innerHTML = `${this.title}${!this.recordCount ? '' : `<span class="result-count">${this.localeAPI.translate('Results').replace('{0}', this.recordCount)}</span>`}`;
  }

  /**
   * Sets the no margins attribute
   * @param {boolean} value The value for no margins attribute
   */
  set noMargins(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.NO_MARGINS, '');
      if (this.input) this.input.setAttribute(attributes.NO_MARGINS, '');
      return;
    }
    this.removeAttribute(attributes.NO_MARGINS);
    if (this.input) this.input.removeAttribute(attributes.NO_MARGINS);
  }

  /**
   * Get the no margins state
   * @returns {boolean} true if margins are disabled
   */
  get noMargins(): boolean {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Set the dropdown size
   * @param {string} value The value
   */
  set size(value: string) {
    if (value) {
      this.setAttribute(attributes.SIZE, value);
    } else {
      this.removeAttribute(attributes.SIZE);
    }
    if (this.triggerField) this.triggerField.setAttribute(attributes.SIZE, this.size);
  }

  /**
   * Get the dropdown size
   * @returns {string} the size (default 'md')
   */
  get size(): string { return this.getAttribute(attributes.SIZE) ?? 'md'; }

  /**
   * Set the string delimiter on selection
   * @param {string} value The field name
   */
  set delimiter(value: string) {
    if (value) {
      this.setAttribute(attributes.DELIMITER, value);
    }
  }

  /**
   * Get the string delimiter on selection
   * @returns {string} the delimiter (default ',')
   */
  get delimiter(): string { return this.getAttribute(attributes.DELIMITER) || ','; }

  /**
   * Get the trigger field reference
   * @returns {IdsTriggerField} the trigger field
   */
  get input() {
    return this.container as IdsTriggerField;
  }

  /**
   * Sets the clearable x button
   * @param {boolean | string} value If true will set to clearable
   */
  set clearable(value: boolean | string) {
    const val = stringToBool(value);
    if (val !== this.state.clearable) {
      this.state.clearable = val;
      this.setAttribute(attributes.CLEARABLE, String(val));
      this.triggerField?.setAttribute(attributes.CLEARABLE, String(val));
    }
  }

  /**
   * Get whether the clearable x button is enabled
   * @returns {boolean | string} true if clearable (default true)
   */
  get clearable(): boolean | string { return this.state.clearable || true; }

  /**
   * Set the placeholder text
   * @param {string} value The placeholder text
   */
  set placeholder(value: string) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value);
      this.triggerField?.setAttribute(attributes.PLACEHOLDER, value);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      this.triggerField?.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the placeholder text
   * @returns {string} the placeholder text
   */
  get placeholder(): string { return this.getAttribute(attributes.PLACEHOLDER) || ''; }

  /**
   * Sets the id internally and externally
   * @param {string} value id value
   */
  set id(value: string) {
    this.shadowRoot?.querySelector('ids-trigger-field')?.setAttribute(attributes.ID, `${value}-trigger-field`);
    this.setAttribute(attributes.ID, value);
  }

  /**
   * Get the id
   * @returns {string} the id (default 'none')
   */
  get id(): string {
    return this.getAttribute(attributes.ID) || 'none';
  }

  private setContainerHeight(height: string | null): void {
    if (this.modal) {
      const container = this.modal.querySelector('.data-grid-parent-container');
      if (container) {
        (container as any).style.height = height || '';
        (container as any).style.overflow = height ? 'auto' : '';
      }
    }
  }

  /**
   * Set the fixed height of the lookup modal
   * @param {string} value The height value (e.g. '400px')
   */
  set height(value: string) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
      this.setContainerHeight(value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.setContainerHeight(null);
    }
  }

  /**
   * Get the fixed height of the lookup modal
   * @returns {string} the height value
   */
  get height(): string {
    return this.getAttribute(attributes.HEIGHT) || '';
  }

  /**
   * Push field-height/compact to the container element
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string) {
    if (val) {
      const attr = val === 'compact' ? { name: 'compact', val: '' } : { name: 'field-height', val };
      this.triggerButton?.setAttribute(attr.name, attr.val);
      this.triggerField?.setAttribute(attr.name, attr.val);
      this.triggerField?.clearableButton?.setAttribute(attr.name, attr.val);
    } else {
      this.triggerButton?.removeAttribute('compact');
      this.triggerButton?.removeAttribute('field-height');
      this.triggerField?.removeAttribute('compact');
      this.triggerField?.removeAttribute('field-height');
      this.triggerField?.clearableButton?.removeAttribute('compact');
      this.triggerField?.clearableButton?.removeAttribute('field-height');
      this.listBox?.removeAttribute('compact');
    }
  }

  /**
   * Set the value in the input for the selected row(s)
   * @private
   */
  #setInputValue(): void {
    this.value = this.selectedValues.join(this.delimiter) || '';
  }

  onTriggerClick: ((e: any) => void) | undefined;

  private selectedValues: string[] = [];

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  #handleEvents() {
    this.onEvent('click.lookup', this.modal, async (e: any) => {
      const btnId = e.target?.getAttribute('id');
      const isCancelButton = e.target?.hasAttribute('cancel') || btnId === 'modal-cancel-btn';
      const isConfirmButton = e.target?.hasAttribute('confirm') || ['modal-confirm-btn', 'modal-apply-btn'].includes(btnId);

      if (isCancelButton) {
        await this.modal?.hide();
        this.#syncSelectedRows();
      } else if (isConfirmButton) {
        this.#setInputValue();
        await this.modal?.hide();
      }
    });

    const triggerFieldUpdated = () => {
      const tfValue = this.triggerField?.value;
      let isSynced = tfValue !== this.value;
      if (!isSynced) {
        const selected = this.dataGrid?.selectedRows?.map((d: any) => d?.data?.[this.field]) || [];
        const values = this.value?.split(this.delimiter) || [];
        if (selected.length !== values.length) isSynced = true;
        if (!isSynced) {
          for (let i = 0, l = values.length; i < l; i++) {
            if (!selected.includes(values[i])) {
              isSynced = true;
              break;
            }
          }
        }
      }
      if (isSynced) this.#syncSelectedRows(tfValue);
    };

    this.offEvent('input.lookup', this.triggerField);
    this.onEvent('input.lookup', this.triggerField, triggerFieldUpdated);
    this.offEvent('change.lookup', this.triggerField);
    this.onEvent('change.lookup', this.triggerField, triggerFieldUpdated);

    let typingTimer: NodeJS.Timeout | undefined;
    const search = (searchTerm: string) => {
      const filter = [
        {
          column: 'all',
          operator: this.searchTermCaseSensitive ? 'equals' : 'contains',
          value: searchTerm
        }
      ];
      this.dataGrid?.filters?.applyFilter(filter);
    };

    this.dataGrid?.addEventListener('filtered', () => {
      const currentValue = this.selectedValues.join(this.delimiter);
      this.#syncSelectedRows(currentValue);
    });

    this.offEvent('input.searchfield', this.searchField, () => clearTimeout(typingTimer));
    this.onEvent('input.searchfield', this.searchField, (e: KeyboardEvent) => {
      clearTimeout(typingTimer);
      if (e.key === 'Enter') {
        search((e.target as HTMLInputElement).value);
        this.triggerEvent('search', this, { detail: { searchTerm: (e.target as HTMLInputElement).value, method: 'enter' } });
      } else {
        typingTimer = setTimeout(() => {
          search(this.searchField?.value ? this.searchField?.value : '');
          this.triggerEvent('search', this, { detail: { searchTerm: this.searchField?.value } });
        }, 200);
      }
    });

    this.onEvent('cleared.searchfield', this.searchField, () => {
      clearTimeout(typingTimer);
      search('');
      this.triggerEvent('cleared', this, { detail: { searchTerm: '', method: 'clear' } });
    });

    this.modal?.addEventListener('beforeshow', ((e: CustomEvent) => {
      // In the case of readonly/disabled or no data do not show the modal
      this.selectedValues = this.value?.split(this.delimiter).filter((v) => v) || [];
      if (this.readonly || this.disabled) {
        e.detail.response(false);
      }
      // Show always for custom modals
      if (this.querySelectorAll('[slot="lookup-modal"]').length === 1) {
        return;
      }
      // In the case of no data do not show the modal
      if (this.data?.length === 0 && this.columns?.length === 1 && (this.columns[0] as any).id === '') {
        e.detail.response(false);
      }
    }) as EventListener);

    // Propagate a few events to the parent
    this.dataGrid?.addEventListener('beforerowselected', ((e: CustomEvent) => {
      this.triggerEvent('beforerowselected', this, { detail: e.detail });
    }) as EventListener);

    this.dataGrid?.addEventListener('rowselected', ((e: CustomEvent) => {
      const selectedRowData = e.detail.data;
      if (selectedRowData) {
        const fieldValue = selectedRowData[this.field];
        if (!this.selectedValues.includes(fieldValue)) {
          this.selectedValues.push(fieldValue);
        }
      }
      this.triggerEvent('rowselected', this, { detail: e.detail });
    }) as EventListener);

    this.dataGrid?.addEventListener('beforerowdeselected', ((e: CustomEvent) => {
      this.triggerEvent('beforerowdeselected', this, { detail: e.detail });
    }) as EventListener);

    this.dataGrid?.addEventListener('rowdeselected', ((e: CustomEvent) => {
      const deselectedRowData = e.detail.data;
      if (deselectedRowData) {
        const fieldValue = deselectedRowData[this.field];
        const index = this.selectedValues.indexOf(fieldValue);
        if (index > -1) {
          this.selectedValues.splice(index, 1);
        }
      }
      this.triggerEvent('rowdeselected', this, { detail: e.detail });
    }) as EventListener);

    this.dataGrid?.addEventListener('selectionchanged', (async (e: CustomEvent) => {
      this.triggerEvent('selectionchanged', this, { detail: e.detail });
      if (this.dataGrid?.rowSelection === 'single' && e.detail.selectedRows?.length > 0) {
        this.#setInputValue();
        await this.modal?.hide();
      }
    }) as unknown as EventListener);

    return this;
  }

  /**
   * Establish Internal Keyboard shortcuts
   * @private
   * @returns {object} This API object for chaining
   */
  #handleKeys() {
    this.listen(['ArrowDown'], this, async () => {
      await this.modal?.show();
    });
    return this;
  }

  /**
   * Push label-position to the container element
   * @returns {void}
   */
  onLabelPositionChange(): void {
    if (this.input) this.input.labelPosition = this.labelPosition;
  }

  /**
   * Push label-width to the container element
   * @returns {void}
   */
  onLabelWidthChange(): void {
    if (this.input) this.input.labelWidth = this.labelWidth;
  }

  /**
   * Push label-align-y to the container element
   * @returns {void}
   */
  onLabelAlignYChange(): void {
    if (this.input) this.input.labelAlignY = this.labelAlignY;
  }

  /**
   * Push label-break to the container element
   * @returns {void}
   */
  onLabelBreakChange(): void {
    if (this.input) this.input.labelBreak = this.labelBreak;
  }
}
